import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Container,
  Button, Divider, Link as MuiLink, Badge, List, ListItem,
  ListItemText, ListItemIcon, Drawer, Tooltip, useTheme,
  LinearProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CallIcon from '@mui/icons-material/Call';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CloseIcon from '@mui/icons-material/Close';
import Logo from './Logo';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { loadCart } from '@utils/cartUtils';
import { getTypeaheadSuggestions } from '@api/search';
import { useFaq } from '@context/FaqContext';
import { isDebugEnabled, toggleConsoleLogs } from '@utils/logger';
import SearchInput from '../search/SearchInput';
import SearchSuggestions from '../search/SearchSuggestions';
import DebugDrawer from '../debug/DebugDrawer';
import { logEvents } from '@utils/eventLogger';
import { searchInputStyles } from '@styles/commonStyles';

const mainCategories = [
  { label: 'Lighting', href: '/category?Lighting' },
  { label: 'Plumbing', href: '/category?Plumbing' },
  { label: 'Home Decor', href: '/category?Home%20Decor' },
  { label: 'Hardware', href: '/category?Hardware' },
  { label: 'Cabinet Hardware', href: '/category?Cabinet%20Hardware' },
  { label: 'Flooring', href: '/category?Flooring' },
  { label: 'Appliances', href: '/category?Appliances' },
  { label: 'HVAC', href: '/category?HVAC' }
];

const Header = () => {
  const theme = useTheme();
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debugDrawerOpen, setDebugDrawerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lastApiCall, setLastApiCall] = useState(null);
  const [logMessages, setLogMessages] = useState([]);
  const [consoleLogsEnabled, setConsoleLogsEnabled] = useState(isDebugEnabled());
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);
  const lastApiCallRef = useRef(null);
  const logRef = useRef([]);
  const { toggleFaqVisibility } = useFaq();
  const location = useLocation();

  useEffect(() => {
    const updateCartItems = () => { setCartItems(loadCart()); };
    updateCartItems();
    window.addEventListener('cartUpdated', updateCartItems);
    return () => { window.removeEventListener('cartUpdated', updateCartItems); };
  }, []);

  // Listen for custom event to update debug info
  useEffect(() => {
    function handleApiCallEvent(e) {
      setLastApiCall(e.detail);
      lastApiCallRef.current = e.detail;
    }
    window.addEventListener('lwApiCall', handleApiCallEvent);
    return () => window.removeEventListener('lwApiCall', handleApiCallEvent);
  }, []);

  // Listen for custom log events
  useEffect(() => {
    function handleLogEvent(e) {
      logRef.current = [...logRef.current, e.detail];
      setLogMessages([...logRef.current]);
    }
    window.addEventListener('lwLog', handleLogEvent);
    return () => window.removeEventListener('lwLog', handleLogEvent);
  }, []);

  // Debounce utility to prevent rapid firing of functions
  const debounce = (func, delay) => { 
    let timeoutId; 
    return function(...args) { 
      if (timeoutId) clearTimeout(timeoutId); 
      timeoutId = setTimeout(() => { 
        func.apply(this, args); 
      }, delay); 
    }; 
  };
  
  // Fetch typeahead suggestions with debounce to prevent too many calls
  const fetchSuggestions = debounce(async (query) => { 
    if (query.length < 2) { 
      setSearchSuggestions([]); 
      return; 
    } 
    setLoading(true); 
    try { 
      const result = await getTypeaheadSuggestions(query); 
      setSearchSuggestions(result.success && result.suggestions.length > 0 ? result.suggestions : []);
      setShowSuggestions(true);
    } catch { 
      setSearchSuggestions([]); 
    } finally { 
      setLoading(false); 
    } 
  }, 300);
  
  // Handle search submission to prevent duplicate calls
  const handleSearchSubmit = (query, device = null, method = null) => {
    if (!query || !query.trim()) return;
    
    // Log search event using the utility
    logEvents.search(query.trim(), device, method);
    
    // Proceed with navigation - this will trigger the search API call
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };
  
  // Optimized suggestion selection handlers
  const handleSuggestionSelect = useCallback((item, device = null) => {
    if (!item?.id) return;
    
    // Log selection event using the utility
    logEvents.suggestion(item, device);
    
    // Navigate to product
    navigate(`/product/${item.id}`);
    
    // Close suggestions after selection
    setShowSuggestions(false);
    setMobileSearchFocused(false);
  }, [navigate]);

  // Optimized suggestion close handler
  const handleSuggestionsClose = useCallback(() => {
    setShowSuggestions(false);
    setMobileSearchFocused(false);
  }, []);

  // Handle typeahead input changes
  const handleTypeaheadChange = (value, device = null) => {
    setSearchValue(value);
    
    if (value && value.length >= 2) {
      fetchSuggestions(value);
      
      // Log typeahead event using the utility
      logEvents.typeahead(value, device);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };
  
  const cartItemCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top utility bar - desktop only */}
      <Box sx={{ 
        backgroundColor: 'background.default', 
        borderTop: `2px solid ${theme.palette.primary.main}`, 
        borderBottom: '1px solid #e0e0e0', 
        display: { xs: 'none', md: 'block' } 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', py: 0.5 }}>
            <MuiLink href="/proaccount" underline="none" sx={{ color: '#63666A', fontSize: 14, display: 'flex', alignItems: 'center', mx: 2 }}>
              <Box sx={{ fontWeight: 700, mr: 0.5 }}>Are you a Pro?</Box>
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                <span style={{ marginRight: 2 }}>Get</span>
                <Box sx={{ color: theme.palette.primary.main, fontWeight: 700, mx: 0.5 }}>PRO</Box>
                <span>Pricing</span>
              </Box>
            </MuiLink>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <MuiLink href="#" underline="none" sx={{ color: '#63666A', fontSize: 14, display: 'flex', alignItems: 'center', mx: 2 }}>
              <ChatIcon sx={{ fontSize: 18, mr: 0.5 }} />
              Chat With Us
            </MuiLink>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <MuiLink 
              href="#" 
              underline="none" 
              sx={{ 
                color:  '#63666A', 
                fontSize: 14, 
                mx: 2,
                fontWeight: 400,
                '&:hover': { cursor: 'pointer' } 
              }}
              onClick={(e) => {
                e.preventDefault();
                toggleFaqVisibility();
              }}
            >
              FAQs
            </MuiLink>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <MuiLink 
              href="/support/faq" 
              underline="none" 
              sx={{ color: '#63666A', fontSize: 14, mx: 2 }}
              onClick={e => {
                e.preventDefault();
                setDebugDrawerOpen(true);
              }}
            >
              Help
            </MuiLink>
          </Box>
        </Container>
      </Box>

      {/* Desktop main header */}
      <Box sx={{ 
        backgroundColor: '#fff', 
        display: { xs: 'none', md: 'block' }, 
        boxShadow: 'none', 
        borderBottom: '1px solid #e0e0e0' 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, pb: 1 }}>
            {/* Logo */}
            <Box sx={{ minWidth: 150, height: 60, display: 'flex', alignItems: 'center' }}>
              <MuiLink href="/" underline="none" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                <Logo height={50} />
              </MuiLink>
            </Box>
            
            {/* Desktop search */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', ml: 0, mr: 4 }}>
              <Box sx={{ width: '100%', maxWidth: '100%', position: 'relative' }}>
                <SearchInput
                  value={searchValue}
                  onChange={(e) => handleTypeaheadChange(e.target.value, 'desktop')}
                  onSubmit={() => handleSearchSubmit(searchValue, 'desktop', 'click')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchValue.trim()) {
                      e.preventDefault();
                      handleSearchSubmit(searchValue, 'desktop', 'keyboard-enter');
                      handleSuggestionsClose();
                    } else if (e.key === 'Escape') {
                      handleSuggestionsClose();
                    }
                  }}
                  placeholder="What are you shopping for?"
                  loading={loading}
                  customStyles={searchInputStyles.desktop.paper}
                />
                
                <SearchSuggestions
                  options={searchSuggestions}
                  visible={showSuggestions && searchSuggestions.length > 0}
                  onItemClick={(item) => handleSuggestionSelect(item, 'desktop')}
                  onClose={handleSuggestionsClose}
                  maxItems={5}
                />
              </Box>
            </Box>
            
            {/* Cart icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title={`${cartItemCount} item${cartItemCount !== 1 ? 's' : ''} in cart`}>
                <IconButton 
                  aria-label="shopping cart" 
                  sx={{ color: '#333' }}
                  component={Link}
                  to="/checkout/cart"
                >
                  <Badge 
                    badgeContent={cartItemCount} 
                    color="primary"
                    overlap="circular"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 10,
                        height: 20,
                        minWidth: 20,
                        padding: '0 4px',
                      }
                    }}
                  >
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile header */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, backgroundColor: '#222', py: 1 }}>
        <Container maxWidth="lg" sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                color="inherit" 
                edge="start" 
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon sx={{ color: 'white' }} />
              </IconButton>
              <Box sx={{ ml: 1, color: 'white', fontSize: 18, fontWeight: 700, height: 40, display: 'flex', alignItems: 'center' }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <Logo height={36} width="auto" />
                </Link>
              </Box>
            </Box>
            <Tooltip title={`${cartItemCount} item${cartItemCount !== 1 ? 's' : ''} in cart`}>
              <IconButton color="inherit" edge="end" component={Link} to="/checkout/cart">
                <Badge badgeContent={cartItemCount} color="primary">
                  <ShoppingCartOutlinedIcon sx={{ color: 'white' }} />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Mobile search bar */}
          <Box sx={{ mt: 1.5, mb: 1.5, position: 'relative' }}>
            <SearchInput
              value={searchValue}
              onChange={(e) => handleTypeaheadChange(e.target.value, 'mobile')}
              onSubmit={() => {
                handleSearchSubmit(searchValue, 'mobile', 'click');
                handleSuggestionsClose();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchValue.trim()) {
                  e.preventDefault();
                  handleSearchSubmit(searchValue, 'mobile', 'keyboard-enter');
                  handleSuggestionsClose();
                } else if (e.key === 'Escape') {
                  e.target.blur();
                  handleSuggestionsClose();
                }
              }}
              placeholder="What are you shopping for?"
              loading={loading}
              customStyles={searchInputStyles.mobile.paper}
              buttonColor="primary"
              onFocus={() => setMobileSearchFocused(true)}
              onBlur={() => setTimeout(() => setMobileSearchFocused(false), 200)}
            />
            
            <SearchSuggestions
              options={searchSuggestions}
              visible={mobileSearchFocused && searchSuggestions.length > 0}
              onItemClick={(item) => handleSuggestionSelect(item, 'mobile')}
              onClose={handleSuggestionsClose}
              maxItems={5}
              maxHeight="60vh"
            />
          </Box>
        </Container>
      </Box>
      
      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{ 
          sx: { 
            width: '80%', 
            maxWidth: 320,
            bgcolor: 'background.paper'
          } 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Menu</Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Mobile Menu Items */}
        <List>
          {/* Search in mobile drawer */}
          <ListItem sx={{ pb: 2 }}>
            <Box sx={{ width: '100%', position: 'relative' }}>
              <SearchInput
                value={searchValue}
                onChange={(e) => handleTypeaheadChange(e.target.value, 'mobile-drawer')}
                onSubmit={() => {
                  handleSearchSubmit(searchValue, 'mobile-drawer', 'click');
                  setMobileMenuOpen(false);
                  handleSuggestionsClose();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchValue.trim()) {
                    e.preventDefault();
                    handleSearchSubmit(searchValue, 'mobile-drawer', 'keyboard-enter');
                    setMobileMenuOpen(false);
                    handleSuggestionsClose();
                  } else if (e.key === 'Escape') {
                    e.target.blur();
                    handleSuggestionsClose();
                  }
                }}
                placeholder="Search products..."
                loading={loading}
                customStyles={searchInputStyles.drawer.paper}
              />
              
              <SearchSuggestions
                options={searchSuggestions}
                visible={showSuggestions && searchSuggestions.length > 0}
                onItemClick={(item) => {
                  handleSuggestionSelect(item, 'mobile-drawer');
                  setMobileMenuOpen(false);
                }}
                onClose={handleSuggestionsClose}
                maxItems={5}
                maxHeight="40vh"
                containerStyles={{ width: '100%' }}
              />
            </Box>
          </ListItem>

          <ListItem 
            button 
            component={Link} 
            to="/search?q=*:*"
            onClick={() => setMobileMenuOpen(false)}
            sx={{ fontWeight: 700 }}
          >
            <ListItemText primary="Shop All Departments" primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItem>
          
          {/* Categories */}
          {mainCategories.map((cat) => (
            <ListItem 
              button 
              key={cat.label} 
              component={Link} 
              to={cat.href}
              onClick={() => setMobileMenuOpen(false)}
            >
              <ListItemText primary={cat.label} />
            </ListItem>
          ))}
          
          <Divider sx={{ my: 1 }} />
          
          {/* FAQs and Help Menu */}
          <ListItem 
            button
            onClick={(e) => {
              e.preventDefault();
              toggleFaqVisibility();
              setMobileMenuOpen(false);
            }}
          >
            <ListItemIcon>
              <QuestionAnswerIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="FAQs" />
          </ListItem>
          
          <ListItem 
            button
            onClick={() => {
              setDebugDrawerOpen(true);
              setMobileMenuOpen(false);
            }}
          >
            <ListItemIcon>
              <HelpOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Help" />
          </ListItem>
          
          <Divider sx={{ my: 1 }} />
          
          {/* Contact Options */}
          <ListItem button component="a" href="tel:(800) 375-3403">
            <ListItemIcon>
              <CallIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="(800) 375-3403" />
          </ListItem>
          
          <ListItem button>
            <ListItemIcon>
              <ChatIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Chat With Us" />
          </ListItem>
          
          <Divider sx={{ my: 1 }} />
          
          <ListItem button component={Link} to="/proaccount">
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ fontWeight: 700, mr: 0.5 }}>Are you a Pro?</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
                    <span style={{ marginRight: 2 }}>Get</span>
                    <Box sx={{ color: theme.palette.primary.main, fontWeight: 700, mx: 0.5 }}>PRO</Box>
                    <span>Pricing</span>
                  </Box>
                </Box>
              } 
            />
          </ListItem>
        </List>
      </Drawer>

      {/* Desktop Category Navigation Bar */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: theme.palette.secondary.main, 
          boxShadow: 'none', 
          borderBottom: `2px solid ${theme.palette.primary.main}`, 
          display: { xs: 'none', md: 'block' },
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 48, px: 0, flexWrap: 'nowrap', overflowX: 'auto' }}>
            {(() => {
              const isShopAllSelected = location.pathname === '/search' && decodeURIComponent(location.search) === '?q=*:*';
              return (
                <Button
                  component={Link}
                  to="/search?q=*:*"
                  sx={{
                    backgroundColor: isShopAllSelected ? 'primary.main' : 'transparent',
                    color: isShopAllSelected ? 'white' : 'white',
                    height: '100%',
                    py: 1,
                    px: 2,
                    borderRadius: 0,
                    fontWeight: 500,
                    fontSize: 15,
                    mr: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: 'primary.main' },
                  }}
                >
                  Shop All Departments
                </Button>
              );
            })()}
            {mainCategories.map((cat) => {
              // Fix: Use URLSearchParams to robustly match the category query param
              let isSelected = false;
              if (location.pathname.startsWith('/category')) {
                const params = new URLSearchParams(location.search);
                // Compare decoded values for robust matching
                isSelected = params.keys().next().value === cat.label;
              }
              return (
                <Button
                  key={cat.label}
                  component={Link}
                  to={cat.href}
                  sx={{
                    color: isSelected ? 'white' : 'white',
                    backgroundColor: isSelected ? 'primary.main' : 'transparent',
                    px: 2,
                    height: '100%',
                    borderRadius: 0,
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  }}
                >
                  {cat.label}
                </Button>
              );
            })}
          </Toolbar>
        </Container>
        
        {/* Loading bar at bottom of navigation */}
        {loading && (
          <LinearProgress 
            sx={{ 
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.primary.main
              }
            }} 
          />
        )}
      </AppBar>

      {/* Debug Drawer Component */}
      <DebugDrawer 
        open={debugDrawerOpen} 
        onClose={() => setDebugDrawerOpen(false)} 
        lastApiCall={lastApiCall}
        logMessages={logMessages}
        consoleLogsEnabled={consoleLogsEnabled}
        onToggleConsoleLogs={(enabled) => {
          setConsoleLogsEnabled(enabled);
          toggleConsoleLogs(enabled);
        }}
        onClearLogs={() => {
          logRef.current = [];
          setLogMessages([]);
        }}
      />
    </Box>
  );
};

export default Header;